import { computed, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  IcLookupCarItemDto,
  IcLookupCostUnitTypeItemDto,
  IcLookupCreditorItemDto,
  IcLookupDto,
  IcLookupEmployeeItemDto,
  IcLookupEventCodeForAbsenceRegistrationItemDto,
  IcLookupPaycodeForExtraPaymentItemDto,
  IcLookupPaycodeForFixedTransactionItemDto,
  IcLookupPensionCompanyItemDto,
  IcLookupTagItemDto,
  IcLookupTaxUnitItemDto,
  IcLookupTypeEnumDto,
  IcLookupUnionItemDto,
  IcLookupWtaItemDto,
  ItemCacheClient,
} from '@data-access/bulk-operations-api';
import {
  WebSocketClient,
  WsIcUpserted,
  WsServerPayloadEnum,
} from '@data-access/bulk-operations-ws';
import {
  EMPTY,
  Subscription,
  delayWhen,
  expand,
  filter,
  finalize,
  iif,
  of,
  reduce,
  take,
} from 'rxjs';

import { lookupReducer } from '../helpers/lookup-reducer';

export abstract class IcServiceBase<
  TItem extends IcGenericLookupTypes,
  TModel extends { id: string },
> {
  protected abstract readonly lookupType: IcLookupTypeEnumDto;

  protected readonly client = inject(ItemCacheClient);
  protected readonly wsClient = inject(WebSocketClient);

  public readonly isInitializedSignal = signal(false);
  protected readonly isInitialized$ = toObservable(this.isInitializedSignal);
  public readonly isLoadingSignal = signal(false);

  private isManualSync = false;

  // Make sure to return new Map on updates
  protected readonly rawValuesSignal = signal<Map<string, TItem>>(new Map());
  public readonly valuesSignal = computed<TModel[]>(() =>
    [...this.rawValuesSignal().values()].map((item) => this.mapToLookupModel(item)),
  );
  public readonly valuesMapSignal = computed<Map<string, TModel>>(
    () => new Map(this.valuesSignal().map((item) => [item.id, item])),
  );

  protected wsUpsertSubscription: Subscription | undefined = undefined;

  constructor() {
    this.subscribeToWsSyncComplete();
    this.subscribeToWsUpsert();
    this.subscribeToWsDelete();
  }

  protected abstract mapToLookupModel(value: TItem): TModel;

  public setCache(lookupValues: TItem[], isInitializing = false) {
    this.rawValuesSignal.set(new Map(lookupValues.map((item) => [item.id, item])));
    if (!isInitializing) {
      this.isInitializedSignal.set(true);
    }
  }

  public upsertCacheItems(newValues: TItem[]) {
    this.rawValuesSignal.update((values) => {
      newValues.forEach((newValue) => {
        const currentValue = values.get(newValue.id);

        // We will remove version if there are no errors
        if (currentValue && currentValue.version > newValue.version) {
          console.error(
            `ItemCache: [${this.lookupType}] received WS message with out-of-date version. ID: ${newValue.id}, v: ${newValue.version} (v: ${currentValue.version})`,
          );
        }

        values.set(newValue.id, newValue);
      });

      return new Map(values);
    });
  }

  public upsertCacheItem(newValue: TItem) {
    this.upsertCacheItems([newValue]);
  }

  public removeCacheItems(ids: string[]) {
    this.rawValuesSignal.update((values) => {
      ids.forEach((id) => values.delete(id));

      return new Map(values);
    });
  }

  public removeCacheItem(id: string) {
    this.rawValuesSignal.update((values) => {
      values.delete(id);
      return new Map(values);
    });
  }

  public startSync() {
    this.client
      .getIcStartSync(this.lookupType)
      .pipe(take(1))
      .subscribe(() => {
        this.isManualSync = true;
      });
  }

  public fetchValues() {
    this.isLoadingSignal.set(true);
    this.client
      .getIcLookup(this.lookupType)
      .pipe(
        take(1),
        expand((response) =>
          response.lookup.nextToken
            ? this.client.getIcLookup(this.lookupType, response.lookup.nextToken)
            : EMPTY,
        ),
        reduce(lookupReducer),
        finalize(() => this.isLoadingSignal.set(false)),
      )
      .subscribe((data) => {
        this.setCache(this.getValuesFromLookupDto(data.lookup));
      });
  }

  protected abstract getValuesFromLookupDto(lookup: IcLookupDto): TItem[];

  protected subscribeToWsUpsert() {
    this.wsUpsertSubscription = this.wsClient
      .getMessagesByType(WsServerPayloadEnum.IcUpserted)
      .pipe(
        filter((msg): msg is WsIcGenericTypes => msg.type === this.lookupType),
        // Hold processing to avoid overwrites
        delayWhen(() =>
          iif(
            () => this.isInitializedSignal(),
            of(true),
            this.isInitialized$.pipe(filter((isInitialized) => isInitialized)),
          ),
        ),
      )
      .subscribe((msg) => {
        this.upsertCacheItems(msg.items as unknown as TItem[]);
      });
  }

  private subscribeToWsSyncComplete() {
    this.wsClient
      .getMessagesByType(WsServerPayloadEnum.IcSyncCompleted)
      .pipe(filter((msg) => msg.type === this.lookupType))
      .subscribe((msg) => {
        if (msg.isInitialization || this.isManualSync) {
          this.fetchValues();
        }
      });
  }

  private subscribeToWsDelete() {
    this.wsClient
      .getMessagesByType(WsServerPayloadEnum.IcDeleted)
      .pipe(
        filter((msg) => msg.type === this.lookupType),
        // Hold processing to avoid overwrites
        delayWhen(() =>
          iif(
            () => this.isInitializedSignal(),
            of(true),
            this.isInitialized$.pipe(filter((isInitialized) => isInitialized)),
          ),
        ),
      )
      .subscribe((msg) => {
        this.removeCacheItems(msg.items.map((item) => item.id));
      });
  }
}

export type IcGenericLookupTypes =
  | IcLookupEmployeeItemDto // Some methods are override in child classes
  | IcLookupTaxUnitItemDto
  | IcLookupCreditorItemDto
  | IcLookupUnionItemDto
  | IcLookupPensionCompanyItemDto
  | IcLookupWtaItemDto
  | IcLookupCarItemDto
  | IcLookupTagItemDto
  | IcLookupPaycodeForExtraPaymentItemDto
  | IcLookupPaycodeForFixedTransactionItemDto
  | IcLookupEventCodeForAbsenceRegistrationItemDto
  | IcLookupCostUnitTypeItemDto;

type WsIcGenericTypes = WsIcUpserted & { items: IcGenericLookupTypes[] };
